// src/pages/ContactPage.js
import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { sendMessage } from "../services/contactMessages.api";
import { apiRequest } from "../services/api";
import { LanguageContext } from "../context/LanguageContext";

function ContactPage() {
  const navigate = useNavigate();
  const { lang } = useContext(LanguageContext);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const whatsappMessage =
    lang === "EN" ? "I need help" : "أحتاج مساعدة";

  const whatsappLink = `https://wa.me/96170203205?text=${encodeURIComponent(
    whatsappMessage
  )}`;

  const TEXT = {
    EN: {
      title: "CONTACT US",
      chat: "Messages / Chat",
      fullName: "Full name *",
      phone: "Phone *",
      email: "Email (optional)",
      subject: "Subject",
      message: "Your message *",
      send: "Send Message",
      sending: "Sending…",
      whatsapp: "I prefer WhatsApp",
    },
    AR: {
      title: "تواصلي معنا",
      chat: "الرسائل / المحادثة",
      fullName: "الاسم الكامل *",
      phone: "رقم الهاتف *",
      email: "البريد الإلكتروني (اختياري)",
      subject: "الموضوع",
      message: "رسالتك *",
      send: "إرسال الرسالة",
      sending: "جاري الإرسال…",
      whatsapp: "أفضل واتساب",
    },
  }[lang];

  /* 🔔 unread admin replies */
  useEffect(() => {
    async function loadUnread() {
      if (!localStorage.getItem("esill-token")) {
        setUnreadCount(0);
        return;
      }
      try {
        const res = await apiRequest("/contact-messages/unread-count");
        setUnreadCount(res.unread || 0);
      } catch {
        setUnreadCount(0);
      }
    }
    loadUnread();
  }, []);

  /* 👤 Prefill form */
  useEffect(() => {
    try {
      const cachedUser = localStorage.getItem("esill-user");
      if (!cachedUser) return;

      const user = JSON.parse(cachedUser);

      setForm((prev) => {
        const rawPhone = user.phone || "";
        const rawEmail = user.email || "";
        const phoneLooksLikeEmail = rawPhone.includes("@");

        return {
          ...prev,
          full_name: prev.full_name || user.full_name || "",
          phone: prev.phone || (phoneLooksLikeEmail ? "" : rawPhone),
          email: prev.email || rawEmail || (phoneLooksLikeEmail ? rawPhone : ""),
        };
      });
    } catch {}
  }, []);

  const submit = async () => {
    if (!form.full_name || !form.phone || !form.message) return;

    setSending(true);
    const res = await sendMessage(form);
    setSending(false);

    navigate("/chat", { state: { messageId: res.id } });
  };

  return (
    <div
      className="w-full min-h-screen bg-[#0F1A12] text-white pt-[95px] px-4 pb-24"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      {/* BACK */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-[110px] text-3xl opacity-70 hover:opacity-100"
        style={lang === "AR" ? { right: 16 } : { left: 16 }}
      >
        ×
      </button>

      {/* TITLE */}
      <h1 className="text-xl font-semibold text-center tracking-wide">
        {TEXT.title}
      </h1>
      <div className="h-[2px] w-16 bg-[#2E5A2A] mx-auto mt-2 mb-8" />

      {/* CHAT CTA */}
      <div className="flex justify-center mb-6">
        <button
          onClick={() => {
            if (!localStorage.getItem("esill-token")) {
              localStorage.setItem("esill-open-login", "1");
              navigate("/");
              return;
            }
            navigate("/chat");
          }}
          className="
            relative flex items-center gap-2
            px-6 py-2.5 rounded-full
            border border-[#2E5A2A]
            text-sm font-semibold
            hover:bg-[#2E5A2A]
            transition
          "
        >
          💬 {TEXT.chat}
          {unreadCount > 0 && (
            <span className="absolute -top-2 -right-2 min-w-[20px] h-[20px] rounded-full bg-red-600 text-white text-[11px] flex items-center justify-center px-1">
              {unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* FORM */}
      <div className="max-w-md mx-auto bg-[#121F17] border border-[#2E5A2A] rounded-lg p-5 space-y-4">
        <Field placeholder={TEXT.fullName} value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />
        <Field placeholder={TEXT.phone} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
        <Field placeholder={TEXT.email} value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
        <Field placeholder={TEXT.subject} value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} />

        <textarea
          rows={4}
          placeholder={TEXT.message}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          className="
            w-full rounded-md px-4 py-3 text-sm
            bg-[#0F1A12] text-white
            border border-[#2E5A2A]
            focus:outline-none
          "
        />

        <button
          onClick={submit}
          disabled={sending}
          className="
            w-full py-3 rounded-md font-semibold text-sm
            bg-[#2E5A2A] hover:brightness-110
            transition active:scale-[0.98]
          "
        >
          {sending ? TEXT.sending : TEXT.send}
        </button>

        <button
          onClick={() => window.open(whatsappLink, "_blank")}
          className="
            w-full py-3
            border border-[#25D366]
            text-[#25D366]
            rounded-md
            font-semibold text-sm
            flex items-center justify-center gap-2
            hover:bg-[#25D366] hover:text-white
            transition
          "
        >
          <img src="/icons/whatsapp.png" alt="WhatsApp" className="w-5 h-5" />
          {TEXT.whatsapp}
        </button>
      </div>
    </div>
  );
}

function Field({ placeholder, value, onChange }) {
  return (
    <input
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="
        w-full rounded-md px-4 py-3 text-sm
        bg-[#0F1A12] text-white
        border border-[#2E5A2A]
        focus:outline-none
      "
    />
  );
}

export default ContactPage;
